<template>
  <div class="phone-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Telephony</p>
        <h1>Phone Numbers</h1>
        <p class="subtitle">
          Create, connect, and assign phone numbers to your AI agents.
        </p>
      </div>

      <button class="primary-btn" @click="openCreatePanel">
        <Plus :size="18" />
        Add phone number
      </button>
    </header>

    <section class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <Phone :size="20" />
        </div>
        <div>
          <p>Total numbers</p>
          <strong>{{ phoneNumbers.length }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <Bot :size="20" />
        </div>
        <div>
          <p>Assigned</p>
          <strong>{{ assignedCount }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <CircleOff :size="20" />
        </div>
        <div>
          <p>Unassigned</p>
          <strong>{{ unassignedCount }}</strong>
        </div>
      </div>
    </section>

    <section class="content-grid">
      <div class="main-card">
        <div class="card-header">
          <div>
            <h2>Your phone numbers</h2>
            <p>Manage numbers already connected to your account.</p>
          </div>

          <button class="ghost-btn" @click="loadPage" :disabled="loading">
            <RefreshCcw :size="16" :class="{ spin: loading }" />
            Refresh
          </button>
        </div>

        <div v-if="loading" class="state-box">
          <Loader2 class="spin" :size="22" />
          <p>Loading phone numbers...</p>
        </div>

        <div v-else-if="error" class="error-box">
          <AlertTriangle :size="20" />
          <div>
            <strong>Unable to load phone numbers</strong>
            <p>{{ error }}</p>
          </div>
        </div>

        <div v-else-if="phoneNumbers.length === 0" class="empty-box">
          <PhoneCall :size="34" />
          <h3>No phone numbers yet</h3>
          <p>Add an existing number or purchase a new one to start receiving calls.</p>
          <button class="primary-btn" @click="openCreatePanel">
            <Plus :size="18" />
            Add phone number
          </button>
        </div>

        <div v-else class="numbers-list">
          <article
            v-for="number in phoneNumbers"
            :key="number.id || number.phone_number"
            class="number-card"
          >
            <div class="number-main">
              <div class="number-icon">
                <PhoneCall :size="20" />
              </div>

              <div>
                <h3>{{ formatPhone(number.phone_number) }}</h3>
                <p>
                  {{ number.provider || 'Twilio' }}
                  <span v-if="number.country">• {{ number.country }}</span>
                  <span v-if="number.capabilities">• {{ formatCapabilities(number.capabilities) }}</span>
                </p>
              </div>
            </div>

            <div class="number-status">
              <span
                class="badge"
                :class="number.agent_id ? 'badge-success' : 'badge-muted'"
              >
                {{ number.agent_id ? 'Assigned' : 'Unassigned' }}
              </span>
            </div>

            <div class="number-agent">
              <label>Assigned agent</label>
              <select
                :value="number.agent_id || ''"
                @change="associateNumber(number, $event.target.value)"
                :disabled="savingId === getNumberKey(number)"
              >
                <option value="">No agent assigned</option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div class="number-actions">
              <button
                class="small-btn"
                @click="copyNumber(number.phone_number)"
              >
                <Copy :size="15" />
                Copy
              </button>

              <button
                v-if="number.agent_id"
                class="small-btn danger-light"
                @click="unassignNumber(number)"
                :disabled="savingId === getNumberKey(number)"
              >
                <Unlink :size="15" />
                Unassign
              </button>

              <button
                class="small-btn danger-light"
                @click="deleteNumber(number)"
                :disabled="savingId === getNumberKey(number)"
              >
                <Trash2 :size="15" />
                Remove
              </button>
            </div>
          </article>
        </div>
      </div>

      <aside class="side-card" :class="{ open: panelOpen }">
        <div class="card-header compact">
          <div>
            <h2>Add a phone number</h2>
            <p>Buy a new number or connect an existing one.</p>
          </div>

          <button class="icon-btn mobile-only" @click="panelOpen = false">
            <X :size="18" />
          </button>
        </div>

        <div class="tabs">
          <button
            :class="{ active: activeTab === 'buy' }"
            @click="activeTab = 'buy'"
          >
            Buy new
          </button>
          <button
            :class="{ active: activeTab === 'connect' }"
            @click="activeTab = 'connect'"
          >
            Connect existing
          </button>
        </div>

        <form v-if="activeTab === 'buy'" class="form" @submit.prevent="searchNumbers">
          <div class="form-row two">
            <div class="form-group">
              <label>Country</label>
              <select v-model="searchForm.country">
                <option value="CA">Canada</option>
                <option value="US">United States</option>
              </select>
            </div>

            <div class="form-group">
              <label>Area code</label>
              <input
                v-model.trim="searchForm.area_code"
                type="text"
                placeholder="514"
                maxlength="6"
              />
            </div>
          </div>

          <div class="form-group">
            <label>Capabilities</label>
            <div class="checkbox-row">
              <label>
                <input v-model="searchForm.voice" type="checkbox" />
                Voice
              </label>
              <label>
                <input v-model="searchForm.sms" type="checkbox" />
                SMS
              </label>
            </div>
          </div>

          <div class="form-group">
            <label>Assign to agent</label>
            <select v-model="searchForm.agent_id">
              <option value="">Assign later</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>

          <button class="primary-btn full" type="submit" :disabled="searching">
            <Loader2 v-if="searching" class="spin" :size="18" />
            <Search v-else :size="18" />
            Search available numbers
          </button>
        </form>

        <form v-else class="form" @submit.prevent="connectExistingNumber">
          <div class="form-group">
            <label>Phone number</label>
            <input
              v-model.trim="connectForm.phone_number"
              type="tel"
              placeholder="+15145551234"
              required
            />
            <small>Use E.164 format, for example +15145551234.</small>
          </div>

          <div class="form-group">
            <label>Provider</label>
            <select v-model="connectForm.provider">
              <option value="twilio">Twilio</option>
              <option value="external">External</option>
            </select>
          </div>

          <div class="form-group">
            <label>Assign to agent</label>
            <select v-model="connectForm.agent_id">
              <option value="">Assign later</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>

          <button class="primary-btn full" type="submit" :disabled="connecting">
            <Loader2 v-if="connecting" class="spin" :size="18" />
            <LinkIcon v-else :size="18" />
            Connect number
          </button>
        </form>

        <div v-if="availableNumbers.length" class="available-section">
          <h3>Available numbers</h3>

          <div class="available-list">
            <article
              v-for="number in availableNumbers"
              :key="number.phone_number"
              class="available-card"
            >
              <div>
                <strong>{{ formatPhone(number.phone_number) }}</strong>
                <p>{{ formatCapabilities(number.capabilities) }}</p>
              </div>

              <button
                class="small-btn success-light"
                @click="purchaseNumber(number)"
                :disabled="purchasingNumber === number.phone_number"
              >
                <Loader2
                  v-if="purchasingNumber === number.phone_number"
                  class="spin"
                  :size="15"
                />
                <ShoppingCart v-else :size="15" />
                Buy
              </button>
            </article>
          </div>
        </div>

        <div v-if="message.text" class="message-box" :class="message.type">
          <CheckCircle v-if="message.type === 'success'" :size="18" />
          <AlertTriangle v-else :size="18" />
          <span>{{ message.text }}</span>
        </div>
      </aside>
    </section>
  </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import * as api from '@/services/api.js';
import {
  AlertTriangle,
  Bot,
  CheckCircle,
  CircleOff,
  Copy,
  Link as LinkIcon,
  Loader2,
  Phone,
  PhoneCall,
  Plus,
  RefreshCcw,
  Search,
  ShoppingCart,
  Trash2,
  Unlink,
  X
} from 'lucide-vue-next';

const loading = ref(false);
const searching = ref(false);
const connecting = ref(false);
const error = ref('');

const panelOpen = ref(false);
const activeTab = ref('buy');
const savingId = ref(null);
const purchasingNumber = ref('');

const phoneNumbers = ref([]);
const agents = ref([]);
const availableNumbers = ref([]);

const message = reactive({
  type: '',
  text: ''
});

const searchForm = reactive({
  country: 'CA',
  area_code: '514',
  voice: true,
  sms: true,
  agent_id: ''
});

const connectForm = reactive({
  phone_number: '',
  provider: 'twilio',
  agent_id: ''
});

const assignedCount = computed(() => phoneNumbers.value.filter(n => n.agent_id).length);
const unassignedCount = computed(() => phoneNumbers.value.length - assignedCount.value);

function normalizeList(data, keys = []) {
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

async function loadPhoneNumbers() {
  const data = await api.phoneNumbers.getAll();
  phoneNumbers.value = normalizeList(data, ['phone_numbers', 'numbers', 'data']);
}

async function loadAgents() {
  const data = await api.agents.getAll();
  agents.value = normalizeList(data, ['agents', 'data']);
}

async function loadPage() {
  loading.value = true;
  error.value = '';

  try {
    await Promise.all([loadPhoneNumbers(), loadAgents()]);
  } catch (err) {
    error.value = err.message || 'Unable to load telephony data.';
  } finally {
    loading.value = false;
  }
}

function openCreatePanel() {
  panelOpen.value = true;
}

async function searchNumbers() {
  searching.value = true;
  availableNumbers.value = [];

  try {
    const data = await api.phoneNumbers.search({
      country: searchForm.country,
      area_code: searchForm.area_code || null,
      capabilities: {
        voice: !!searchForm.voice,
        sms: !!searchForm.sms
      }
    });

    availableNumbers.value = normalizeList(data, ['available_numbers', 'numbers', 'data']);

    if (!availableNumbers.value.length) {
      showMessage('warning', 'No available numbers found for this search.');
    }
  } catch (err) {
    showMessage('error', err.message || 'Unable to search Twilio phone numbers.');
  } finally {
    searching.value = false;
  }
}

async function purchaseNumber(number) {
  purchasingNumber.value = number.phone_number;

  try {
    const data = await api.phoneNumbers.purchase({
      phone_number: number.phone_number,
      country: searchForm.country,
      region: number.region || null,
      city: number.city || null,
      capabilities: number.capabilities || {
        voice: !!searchForm.voice,
        sms: !!searchForm.sms
      },
      agent_id: searchForm.agent_id || null
    });

    showMessage('success', 'Phone number purchased and connected to Twilio voice.');
    availableNumbers.value = availableNumbers.value.filter(
      item => item.phone_number !== number.phone_number
    );

    await loadPhoneNumbers();

    const created = data.phone_number || data.number || number.phone_number;
    if (created) {
      await copyNumber(typeof created === 'string' ? created : created.phone_number);
    }
  } catch (err) {
    showMessage('error', err.message || 'Unable to purchase phone number.');
  } finally {
    purchasingNumber.value = '';
  }
}

async function connectExistingNumber() {
  connecting.value = true;

  try {
    await api.phoneNumbers.connect({
      phone_number: connectForm.phone_number,
      provider: connectForm.provider,
      capabilities: { voice: true, sms: connectForm.provider === 'twilio' },
      agent_id: connectForm.agent_id || null
    });

    showMessage('success', 'Phone number connected and voice webhook configured.');
    connectForm.phone_number = '';
    connectForm.provider = 'twilio';
    connectForm.agent_id = '';

    await loadPhoneNumbers();
  } catch (err) {
    showMessage('error', err.message || 'Unable to connect phone number.');
  } finally {
    connecting.value = false;
  }
}

async function associateNumber(number, agentId) {
  const key = getNumberKey(number);
  savingId.value = key;

  const previousAgentId = number.agent_id;
  number.agent_id = agentId || null;

  try {
    await api.phoneNumbers.associate({
      phone_number_id: number.id || null,
      phone_number: number.phone_number,
      agent_id: agentId || null
    });

    showMessage('success', agentId ? 'Phone number assigned successfully.' : 'Phone number unassigned successfully.');
    await loadPhoneNumbers();
  } catch (err) {
    number.agent_id = previousAgentId;
    showMessage('error', err.message || 'Unable to update phone number.');
  } finally {
    savingId.value = null;
  }
}

async function unassignNumber(number) {
  await associateNumber(number, '');
}

async function deleteNumber(number) {
  const key = getNumberKey(number);
  savingId.value = key;

  try {
    await api.phoneNumbers.delete(number.id);
    phoneNumbers.value = phoneNumbers.value.filter(item => getNumberKey(item) !== key);
    showMessage('success', 'Phone number removed.');
  } catch (err) {
    showMessage('error', err.message || 'Unable to remove phone number.');
  } finally {
    savingId.value = null;
  }
}

async function copyNumber(value) {
  if (!value) return;

  try {
    await navigator.clipboard.writeText(value);
    showMessage('success', 'Phone number copied.');
  } catch (_) {
    showMessage('warning', 'Unable to copy automatically.');
  }
}

function showMessage(type, text) {
  message.type = type;
  message.text = text;

  window.clearTimeout(showMessage.timer);
  showMessage.timer = window.setTimeout(() => {
    message.type = '';
    message.text = '';
  }, 4500);
}

function getNumberKey(number) {
  return String(number.id || number.phone_number);
}

function formatPhone(value) {
  if (!value) return 'Unknown number';
  return value;
}

function formatCapabilities(value) {
  if (!value) return 'Voice';

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return formatCapabilities(parsed);
    } catch (_) {
      return value;
    }
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  const enabled = Object.entries(value)
    .filter(([, enabled]) => !!enabled)
    .map(([key]) => key.toUpperCase());

  return enabled.length ? enabled.join(', ') : 'Voice';
}

onMounted(loadPage);
</script>

<style scoped>
.phone-page {
  min-height: 100vh;
  background: #f6f7fb;
  padding: 32px;
  color: #111827;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #635bff;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin: 0;
}

h1 {
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.subtitle {
  margin-top: 10px;
  color: #6b7280;
  font-size: 16px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card,
.main-card,
.side-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
}

.stat-icon,
.number-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 16px;
  background: #eef2ff;
  color: #4f46e5;
}

.stat-card p {
  color: #6b7280;
  font-size: 13px;
  font-weight: 700;
}

.stat-card strong {
  display: block;
  margin-top: 2px;
  font-size: 26px;
  letter-spacing: -0.04em;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 24px;
  align-items: start;
}

.main-card,
.side-card {
  padding: 22px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.card-header.compact {
  margin-bottom: 16px;
}

.card-header h2 {
  font-size: 22px;
  letter-spacing: -0.03em;
}

.card-header p {
  margin-top: 5px;
  color: #6b7280;
  font-size: 14px;
}

.primary-btn,
.ghost-btn,
.small-btn,
.icon-btn {
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 800;
  transition: 0.18s ease;
}

.primary-btn {
  background: #111827;
  color: white;
  border-radius: 999px;
  padding: 13px 18px;
}

.primary-btn:hover {
  transform: translateY(-1px);
  background: #000;
}

.primary-btn.full {
  width: 100%;
}

.ghost-btn,
.small-btn,
.icon-btn {
  background: #f3f4f6;
  color: #111827;
  border-radius: 999px;
}

.ghost-btn {
  padding: 10px 14px;
}

.small-btn {
  padding: 9px 12px;
  font-size: 13px;
}

.icon-btn {
  width: 38px;
  height: 38px;
}

button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none !important;
}

.state-box,
.empty-box,
.error-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  border: 1px dashed #d1d5db;
  border-radius: 20px;
  color: #6b7280;
  text-align: center;
}

.empty-box {
  flex-direction: column;
}

.empty-box h3 {
  color: #111827;
  font-size: 20px;
}

.error-box {
  justify-content: flex-start;
  color: #b91c1c;
  background: #fef2f2;
  border-color: #fecaca;
  text-align: left;
}

.error-box p {
  margin-top: 4px;
  color: #991b1b;
}

.numbers-list {
  display: grid;
  gap: 14px;
}

.number-card {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) auto minmax(220px, 280px) auto;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #fff;
}

.number-main {
  display: flex;
  align-items: center;
  gap: 14px;
}

.number-main h3 {
  font-size: 18px;
  letter-spacing: -0.02em;
}

.number-main p {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 900;
}

.badge-success {
  color: #047857;
  background: #d1fae5;
}

.badge-muted {
  color: #6b7280;
  background: #f3f4f6;
}

.number-agent label,
.form-group label {
  display: block;
  margin-bottom: 7px;
  color: #374151;
  font-size: 13px;
  font-weight: 800;
}

select,
input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  padding: 12px 13px;
  background: white;
  color: #111827;
  font-size: 14px;
  outline: none;
}

select:focus,
input:focus {
  border-color: #635bff;
  box-shadow: 0 0 0 4px rgba(99, 91, 255, 0.12);
}

.number-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.danger-light {
  color: #b91c1c;
  background: #fee2e2;
}

.success-light {
  color: #047857;
  background: #d1fae5;
}

.tabs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 4px;
  border-radius: 999px;
  background: #f3f4f6;
  margin-bottom: 18px;
}

.tabs button {
  border: none;
  background: transparent;
  border-radius: 999px;
  padding: 10px;
  font-weight: 900;
  color: #6b7280;
  cursor: pointer;
}

.tabs button.active {
  background: white;
  color: #111827;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}

.form {
  display: grid;
  gap: 15px;
}

.form-row.two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-group small {
  display: block;
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
}

.checkbox-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.checkbox-row label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.checkbox-row input {
  width: auto;
}

.available-section {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.available-section h3 {
  margin-bottom: 12px;
  font-size: 16px;
}

.available-list {
  display: grid;
  gap: 10px;
}

.available-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.available-card p {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

.message-box {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  padding: 13px;
  border-radius: 16px;
  font-weight: 700;
  font-size: 14px;
}

.message-box.success {
  color: #047857;
  background: #ecfdf5;
}

.message-box.error,
.message-box.warning {
  color: #b91c1c;
  background: #fef2f2;
}

.spin {
  animation: spin 0.9s linear infinite;
}

.mobile-only {
  display: none;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .number-card {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .number-status,
  .number-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 760px) {
  .phone-page {
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
  }

  h1 {
    font-size: 30px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .form-row.two {
    grid-template-columns: 1fr;
  }

  .mobile-only {
    display: inline-flex;
  }
}
</style>
